import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type ExamType   = 'Unit Test' | 'Mid-Term' | 'Pre-Board' | 'Quarterly' | 'Half-Yearly' | 'Final / Annual';
export type ExamStatus = 'Draft' | 'Upcoming' | 'Ongoing' | 'Completed' | 'Results Declared' | 'Cancelled';
export type ExamTab    = 'exams' | 'schedule' | 'marks' | 'results' | 'reports';
export type ViewMode   = 'list' | 'form' | 'detail';
export type ReportView = 'charts' | 'list';

export interface ExamScheduleRow {
  subject:     string;
  date:        string;
  start_time:  string;
  duration:    number;   // minutes
  max_marks:   number;
  pass_marks:  number;
  room_no:     string;
}

export interface Exam {
  id:            number;
  exam_name:     string;
  exam_type:     ExamType;
  class_name:    string;
  academic_year: string;
  start_date:    string;
  end_date:      string;
  description:   string;
  status:        ExamStatus;
  schedule:      ExamScheduleRow[];
  created_at:    string;
}

export interface SubjectMark {
  subject:    string;
  max_marks:  number;
  pass_marks: number;
  marks_obtained: number | null;
  is_absent:  boolean;
  grade:      string;
  remarks:    string;
}

export interface StudentResult {
  id:             number;
  exam_id:        number;
  student_id:     string;
  student_name:   string;
  class_name:     string;
  roll_no:        string;
  subject_marks:  SubjectMark[];
  total_marks:    number;
  max_total:      number;
  percentage:     number;
  grade:          string;
  rank:           number;
  result:         'Pass' | 'Fail' | 'Absent' | 'Withheld';
  remarks:        string;
  is_published:   boolean;
}

export interface GradeRule {
  min: number; max: number; grade: string; gpa: number; color: string; label: string;
}

@Component({
  selector:    'app-examination',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './examination.html',
  styleUrl:    './examination.scss',
})
export class Examination implements OnInit {
  dataService     = inject(DataService);
  loading = false;
  user: any = null;

  activeTab       = signal<ExamTab>('exams');
  saveSuccess     = signal('');
  showDeleteModal = signal(false);
  deleteCtx       = signal<{ id: number; label: string; type: string } | null>(null);
  showHallTicket  = signal(false);
  hallTicketData  = signal<StudentResult | null>(null);

  // Exam CRUD
  examMode        = signal<ViewMode>('list');
  editingExamId   = signal<number | null>(null);
  examSubmitted   = false;
  examForm: Partial<Exam> = {};

  // Filters — signals so computed lists actually refresh
  examSearch       = signal('');
  examClassFilter  = signal('');
  examTypeFilter   = signal('');
  examStatusFilter = signal('');

  // Schedule / Marks tab
  selectedExamId  = signal<number>(0);
  marksClass      = signal('');
  marksSearch     = signal('');
  marksSubmitted  = false;
  isNewMarks      = signal(false);
  editingMarks    = signal<StudentResult | null>(null);

  // Schedule add form
  showScheduleForm = signal(false);
  scheduleSubmitted = false;
  scheduleForm: Partial<ExamScheduleRow> = {};

  // Results tab
  resultExamId    = signal<number>(1);
  resultSearch    = signal('');
  publishConfirm  = signal(false);

  // Reports tab
  reportExamId       = signal<number>(1);
  reportSearch       = signal('');
  reportView         = signal<ReportView>('list');
  marksheetStudent   = signal<StudentResult | null>(null);
  students4marks     = [{'roll_no': '', 'name': ''}];

  // ── LOOKUPS ──────────────────────────────────────────
  readonly examTypes: ExamType[] = ['Unit Test','Mid-Term','Quarterly','Half-Yearly','Pre-Board','Final / Annual'];
  readonly examStatuses: ExamStatus[] = ['Draft','Upcoming','Ongoing','Completed','Results Declared','Cancelled'];
  readonly classOptions = this.dataService.classes;
  readonly academicYears = ['2024-25', '2025-26', '2026-27', '2027-28', '2028-29', '2029-30'];
  readonly subjectMap: Record<string, string[]> = {
    '10c': ['Physics','Chemistry','Biology','Mathematics','English','Physical Education'],
    '11c': ['Accountancy','Business Studies','Economics','Mathematics','English'],
    '12c': ['Science','Mathematics','English','Hindi','Social Science'],
    'BCA': ['Mathematics','C Programming','Data Structures','DBMS','English'],
  };
  readonly defaultSubjects = this.dataService.subjects;
  readonly roomOptions = this.dataService.class_rooms;

  readonly gradeRules: GradeRule[] = [
    { min:90, max:100, grade:'A+', gpa:10.0, color:'#166534', label:'Outstanding'  },
    { min:80, max:89,  grade:'A',  gpa:9.0,  color:'#15803d', label:'Excellent'    },
    { min:70, max:79,  grade:'B+', gpa:8.0,  color:'#0284c7', label:'Very Good'    },
    { min:60, max:69,  grade:'B',  gpa:7.0,  color:'#1d4ed8', label:'Good'         },
    { min:50, max:59,  grade:'C+', gpa:6.0,  color:'#7c3aed', label:'Above Average'},
    { min:40, max:49,  grade:'C',  gpa:5.0,  color:'#d97706', label:'Average'      },
    { min:33, max:39,  grade:'D',  gpa:4.0,  color:'#ea580c', label:'Pass'         },
    { min:0,  max:32,  grade:'F',  gpa:0,    color:'#dc2626', label:'Fail'         },
  ];

  private nextExamId   = 4;
  private nextResultId = 30;

  // ── DATA ─────────────────────────────────────────────
  exams   = signal<Exam[]>([]);             //(this.buildExams());
  results = signal<StudentResult[]>([]);    //(this.buildResults());

  // ── COMPUTED ─────────────────────────────────────────
  readonly filteredExams = computed(() => {
    const q = this.examSearch().toLowerCase().trim();
    const classF  = this.examClassFilter();
    const typeF   = this.examTypeFilter();
    const statusF = this.examStatusFilter();
    return this.exams().filter(e =>
      (!q || e.exam_name.toLowerCase().includes(q) || e.class_name.toLowerCase().includes(q) || e.exam_type.toLowerCase().includes(q)) &&
      (!classF  || e.class_name === classF) &&
      (!typeF   || e.exam_type  === typeF)  &&
      (!statusF || e.status     === statusF)
    );
  });

  readonly selectedExam = computed(() => this.exams().find(e => e.id === this.selectedExamId()) ?? null);
  readonly resultExam   = computed(() => this.exams().find(e => e.id === this.resultExamId()) ?? null);
  readonly reportExam   = computed(() => this.exams().find(e => e.id === this.reportExamId()) ?? null);

  readonly examResults = computed(() => {
    const q = this.resultSearch().toLowerCase().trim();
    return this.results().filter(r => r.exam_id === this.resultExamId())
      .filter(r => !q || r.student_name.toLowerCase().includes(q) || r.student_id.toString().toLowerCase().includes(q) || r.roll_no.toLowerCase().includes(q))
      .sort((a,b) => a.rank - b.rank);
  });

  readonly marksResults = computed(() => {
    const q = this.marksSearch().toLowerCase().trim();
    return this.results().filter(r =>
      r.exam_id === this.selectedExamId() &&
      r.class_name === this.marksClass() &&
      (!q || r.student_name.toLowerCase().includes(q) || r.roll_no.toLowerCase().includes(q) || r.student_id.toString().toLowerCase().includes(q))
    ).sort((a,b) => a.roll_no.localeCompare(b.roll_no));
  });

  readonly reportResults = computed(() => {
    const q = this.reportSearch().toLowerCase().trim();
    return this.results().filter(r => r.exam_id === this.reportExamId())
      .filter(r => !q || r.student_name.toLowerCase().includes(q) || r.student_id.toString().toLowerCase().includes(q) || r.roll_no.toLowerCase().includes(q))
      .sort((a,b) => a.rank - b.rank);
  });

  readonly examStats = computed(() => {
    const list = this.exams();
    return {
      total:     list.length,
      upcoming:  list.filter(e => e.status === 'Upcoming').length,
      ongoing:   list.filter(e => e.status === 'Ongoing').length,
      completed: list.filter(e => e.status === 'Completed' || e.status === 'Results Declared').length,
    };
  });

  readonly resultStats = computed(() => {
    const list = this.examResults();
    if (!list.length) return { pass:0, fail:0, avg:0, highest:0, passRate:0 };
    const passed  = list.filter(r => r.result === 'Pass').length;
    const avg     = Math.round(list.reduce((s,r) => s + r.percentage, 0) / list.length);
    const highest = Math.max(...list.map(r => r.percentage));
    return { pass: passed, fail: list.length - passed, avg, highest, passRate: Math.round((passed/list.length)*100) };
  });

  readonly gradeDistribution = computed(() => {
    const list = this.examResults();
    const grades = ['A+','A','B+','B','C+','C','D','F'];
    return grades.map(g => ({
      grade: g,
      count: list.filter(r => r.grade === g).length,
      pct: list.length ? Math.round((list.filter(r => r.grade === g).length / list.length) * 100) : 0,
      color: this.gradeRules.find(gr => gr.grade === g)?.color ?? '#6b7280',
    })).filter(g => g.count > 0);
  });

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");

    if(erp_rem && !this.user) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
    }

    this.loading = true;
    await this.dataService.getAllRecords('exams').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.exams.set(res);
      }
    });
    
    this.loading = true;
    await this.dataService.getAllRecords('exam_results').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.results.set(res);
      }
    });
  }

  // ── FILTER / SELECT HELPERS ───────────────────────────
  onSelectedExamChange(id: string | number): void {
    const n = Number(id);
    this.selectedExamId.set(n);
    const exam = this.exams().find(e => e.id === n);
    if (exam) this.marksClass.set(exam.class_name);
    this.marksSearch.set('');
  }

  onResultExamChange(id: string | number): void {
    this.resultExamId.set(Number(id));
    this.resultSearch.set('');
  }

  onReportExamChange(id: string | number): void {
    this.reportExamId.set(Number(id));
    this.reportSearch.set('');
    this.marksheetStudent.set(null);
  }

  resetFilters(): void {
    this.examSearch.set('');
    this.examClassFilter.set('');
    this.examTypeFilter.set('');
    this.examStatusFilter.set('Draft');
  }

  // ── EXAM CRUD ─────────────────────────────────────────
  openNewExam(): void {
    // const subjects: any = this.subjectMap['12c'] ?? this.defaultSubjects;
    const subjects: any = this.defaultSubjects;
    this.examForm = {
      exam_name:'', exam_type:'Mid-Term', class_name:'12c',
      academic_year:'', start_date:'', end_date:'', description:'',
      status:'Upcoming', created_at: new Date().toISOString().substring(0,10),
      schedule: [{ subject:'', date:'', start_time:'09:00', duration:180, max_marks:100, pass_marks:33, room_no:'' }],
    };
    this.editingExamId.set(null); this.examSubmitted = false;
    this.examMode.set('form');
  }

  editExam(e: Exam): void {
    this.examForm = JSON.parse(JSON.stringify(e));
    this.editingExamId.set(e.id); this.examSubmitted = false;
    this.examMode.set('form');
  }

  viewExam(e: Exam): void { this.editingExamId.set(e.id); this.examMode.set('detail'); }

  onClassChange(): void {
    const subjects = this.subjectMap[this.examForm.class_name ?? ''] ?? this.defaultSubjects;
    this.examForm.schedule = subjects.map(s => ({
      subject:s, date:'', start_time:'09:00', duration:180, max_marks:100, pass_marks:33, room_no:'',
    }));
  }

  addScheduleRow(): void {
    this.examForm.schedule?.push({ subject:'', date:'', start_time:'09:00', duration:180, max_marks:100, pass_marks:33, room_no:'' });
  }

  removeScheduleRow(i: number): void { this.examForm.schedule?.splice(i, 1); }

  saveExam(): void {
    this.examSubmitted = true;
    if (!this.examForm.exam_name || !this.examForm.class_name || !this.examForm.start_date) return;
    const exam: Exam = {
      id:            this.editingExamId() ?? this.nextExamId++,
      exam_name:     this.examForm.exam_name!,
      exam_type:     this.examForm.exam_type ?? 'Mid-Term',
      class_name:    this.examForm.class_name!,
      academic_year: this.examForm.academic_year ?? '2025-26',
      start_date:    this.examForm.start_date!,
      end_date:      this.examForm.end_date ?? this.examForm.start_date!,
      description:   this.examForm.description ?? '',
      status:        this.examForm.status ?? 'Upcoming',
      schedule:      this.examForm.schedule ?? [],
      created_at:    new Date().toISOString().substring(0,10),
    };

    let examData: any = {...exam};
    examData.schedule = JSON.stringify(examData.schedule);

    if (this.editingExamId() !== null) {
      this.doUpdate('exams', examData);
      this.exams.update(l => l.map(e => e.id === exam.id ? exam : e));
    } else {
      this.doSave('exams', examData);
      this.exams.update(l => [...l, exam]);
    }

    this.examMode.set('list'); this.flash('Exam saved!');
  }

  confirmDelete(type: string, id: number, label: string): void {
    this.deleteCtx.set({type,id,label}); this.showDeleteModal.set(true);
  }

  executeDelete(): void {
    const ctx = this.deleteCtx();
    if (!ctx) return;
    if (ctx.type === 'exam') {
      this.exams.update(l => l.filter(e => e.id !== ctx.id));
      this.results.update(l => l.filter(r => r.exam_id !== ctx.id));
    }
    if (ctx.type === 'result') this.results.update(l => this.reassignRanks(l.filter(r => r.id !== ctx.id)));
    this.showDeleteModal.set(false); this.deleteCtx.set(null);
    this.flash('Deleted successfully.');
  }

  toggleExamStatus(e: Exam): void {
    const next: ExamStatus[] = ['Draft','Upcoming','Ongoing','Completed','Results Declared'];
    const idx = next.indexOf(e.status);
    const newStatus = next[(idx + 1) % next.length];
    this.exams.update(l => l.map(x => x.id === e.id ? {...x, status: newStatus} : x));
  }

  // ── SCHEDULE ADD ──────────────────────────────────────
  openNewSchedule(): void {
    const exam = this.selectedExam();
    if (!exam) return;
    this.scheduleForm = {
      subject:'', date: exam.start_date, start_time:'09:00',
      duration:180, max_marks:100, pass_marks:33, room_no:'',
    };
    this.scheduleSubmitted = false;
    this.showScheduleForm.set(true);
  }

  saveSchedule(): void {
    this.scheduleSubmitted = true;
    if (!this.scheduleForm.subject?.trim() || !this.scheduleForm.date) return;
    const examId = this.selectedExamId();
    const row: ExamScheduleRow = {
      subject:    this.scheduleForm.subject!.trim(),
      date:       this.scheduleForm.date!,
      start_time: this.scheduleForm.start_time || '09:00',
      duration:   Number(this.scheduleForm.duration) || 180,
      max_marks:  Number(this.scheduleForm.max_marks) || 100,
      pass_marks: Number(this.scheduleForm.pass_marks) || 33,
      room_no:    this.scheduleForm.room_no ?? '',
    };
    this.exams.update(list => list.map(e =>
      e.id === examId ? { ...e, schedule: [...e.schedule, row] } : e
    ));

    this.exams()?.filter((obj: any) => {
      if(obj.id == examId) {
        let examData: any = {...obj};
        examData.schedule = JSON.stringify(examData.schedule);

        this.doUpdate('exams', examData);
      }
    });


    this.showScheduleForm.set(false);
    this.flash(`Subject “${row.subject}” added to schedule.`);
  }

  // ── MARKS ENTRY ───────────────────────────────────────
  openMarkEntry(r: StudentResult): void {
    this.isNewMarks.set(false);
    this.marksSubmitted = false;
    this.editingMarks.set(JSON.parse(JSON.stringify(r)));
  }

  async openNewMarkEntry() {
    const exam = this.selectedExam();
    if (!exam) return;
    const blank: StudentResult = {
      id: 0,
      exam_id: exam.id,
      student_id: '',
      student_name: '',
      class_name: exam.class_name,
      roll_no: '',
      subject_marks: exam.schedule.map(s => ({
        subject: s.subject,
        max_marks: s.max_marks,
        pass_marks: s.pass_marks,
        marks_obtained: null,
        is_absent: false,
        grade: '',
        remarks: '',
      })),
      total_marks: 0,
      max_total: exam.schedule.reduce((s, r) => s + r.max_marks, 0),
      percentage: 0,
      grade: '',
      rank: 0,
      result: 'Pass',
      remarks: '',
      is_published: false,
    };
    this.marksClass.set(exam.class_name);
    this.isNewMarks.set(true);
    this.marksSubmitted = false;
    this.editingMarks.set(blank);

    this.loading = true;
    await this.dataService.getAllRecords('admissions').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.students4marks = [];

        for(let i=0; i<res.length; i++) {
          const cls4marks = this.marksClass();
          if(res[i].class_name==cls4marks) {
            this.students4marks.push({'roll_no': res[i].roll_no, 'name': res[i].std_name});
          }
        }
      }
    });
  }

  saveMarks(): void {
    const r = this.editingMarks();
    if (!r) return;
    if (this.isNewMarks() && (!r.student_name?.trim() || !r.roll_no?.trim())) {
      this.marksSubmitted = true;
      return;
    }
    const updated = this.computeResult({
      ...r,
      student_name: r.student_name.trim(),
      roll_no: r.roll_no.trim(),
      student_id: r.student_id.toString().trim() || `S${String(this.nextResultId).padStart(3,'0')}`,
      class_name: r.class_name || this.marksClass(),
      exam_id: this.selectedExamId(),
    });

    let stdResult: any = { ...updated };
    stdResult.subject_marks = JSON.stringify(stdResult.subject_marks);
    
    if (this.isNewMarks()) {
      const saved = { ...updated, id: this.nextResultId++ };

      const rSaved = { ...stdResult, id: this.nextResultId++ };
      this.doSave('exam_results', rSaved);

      this.results.update(l => this.reassignRanks([...l, saved]));
      this.flash(`Marks added for ${saved.student_name}!`);
    } else {
      this.doUpdate('exam_results', stdResult);

      this.results.update(l => this.reassignRanks(l.map(x => x.id === r.id ? updated : x)));
      this.flash(`Marks updated for ${r.student_name}!`);
    }

    this.editingMarks.set(null);
    this.isNewMarks.set(false);
  }

  closeMarksModal(): void {
    this.editingMarks.set(null);
    this.isNewMarks.set(false);
    this.marksSubmitted = false;
  }

  private computeResult(r: StudentResult): StudentResult {
    const sms = r.subject_marks;
    const total = sms.reduce((s, m) => s + (m.is_absent ? 0 : (m.marks_obtained ?? 0)), 0);
    const maxTotal = sms.reduce((s, m) => s + m.max_marks, 0);
    const pct = maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
    const grade = this.calcGrade(pct);
    const allPass = sms.every(m => m.is_absent || (m.marks_obtained ?? 0) >= m.pass_marks);
    const allAbsent = sms.length > 0 && sms.every(m => m.is_absent);
    sms.forEach(m => {
      if (m.is_absent) m.grade = 'AB';
      else m.grade = this.calcGrade(m.max_marks > 0 ? Math.round(((m.marks_obtained ?? 0) / m.max_marks) * 100) : 0);
    });
    return {
      ...r,
      total_marks: total,
      max_total: maxTotal,
      percentage: pct,
      grade,
      result: allAbsent ? 'Absent' : allPass ? 'Pass' : 'Fail',
    };
  }

  private reassignRanks(list: StudentResult[]): StudentResult[] {
    const byExam = new Map<number, StudentResult[]>();
    for (const r of list) {
      const arr = byExam.get(r.exam_id) ?? [];
      arr.push(r);
      byExam.set(r.exam_id, arr);
    }
    const rankById = new Map<number, number>();
    byExam.forEach(arr => {
      [...arr].sort((a, b) => b.percentage - a.percentage).forEach((r, i) => rankById.set(r.id, i + 1));
    });
    return list.map(r => ({ ...r, rank: rankById.get(r.id) ?? r.rank }));
  }

  calcGrade(pct: number): string {
    return this.gradeRules.find(g => pct >= g.min && pct <= g.max)?.grade ?? 'F';
  }

  calcGPA(pct: number): number {
    return this.gradeRules.find(g => pct >= g.min && pct <= g.max)?.gpa ?? 0;
  }

  getGradeColor(grade: string): string {
    return this.gradeRules.find(g => g.grade === grade)?.color ?? '#6b7280';
  }

  getGradeLabel(grade: string): string {
    return this.gradeRules.find(g => g.grade === grade)?.label ?? '';
  }

  // ── PUBLISH RESULTS ───────────────────────────────────
  publishResults(): void {
    this.results()?.find((obj: any) => {
      if(obj.exam_id === this.resultExamId()) {
        obj.is_published = true;
        obj.subject_marks = JSON.stringify(obj.subject_marks);

        this.doUpdate('exam_results', obj);
      }
    });
    this.exams()?.find((obj: any) => {
      if(obj.id === this.resultExamId()) {
        obj.status = 'Results Declared';
        obj.schedule = JSON.stringify(obj.schedule);

        this.doUpdate('exams', obj);
      }
    });

    this.results.update(l => l.map(r => r.exam_id === this.resultExamId() ? {...r, is_published:true} : r));
    this.exams.update(l => l.map(e => e.id === this.resultExamId() ? {...e, status:'Results Declared'} : e));

    this.publishConfirm.set(false);
    this.flash('Results published successfully!');
  }

  // ── HALL TICKET / MARKSHEET ───────────────────────────
  openHallTicket(r: StudentResult): void {
    this.hallTicketData.set(r); this.showHallTicket.set(true);
  }

  printHallTicket(): void { window.print(); }

  openMarksheet(r: StudentResult): void {
    this.marksheetStudent.set(r);
    this.reportView.set('list');
  }

  closeMarksheet(): void { this.marksheetStudent.set(null); }

  printMarksheet(): void { window.print(); }

  // ── HELPERS ───────────────────────────────────────────
  getViewExam(): Exam | undefined { return this.exams().find(e => e.id === this.editingExamId()); }
  getExamStatusClass(s: ExamStatus): string { return {Draft: 'es-upcoming',Upcoming:'es-upcoming',Ongoing:'es-ongoing',Completed:'es-done','Results Declared':'es-published',Cancelled:'es-cancelled'}[s]??''; }
  getResultClass(r: string): string { return {Pass:'rc-pass',Fail:'rc-fail',Absent:'rc-absent',Withheld:'rc-withheld'}[r]??''; }
  getExamTypeColor(t: ExamType): string { return {'Unit Test':'#0284c7','Mid-Term':'#7c3aed','Quarterly':'#059669','Half-Yearly':'#d97706','Pre-Board':'#dc2626','Final / Annual':'#1a3a6b'}[t]??'#374151'; }

  formatDate(s: string): string { if(!s) return '—'; return new Date(s).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); }
  formatTime(t: string): string {
    if(!t) return ''; const [h,m] = t.split(':'); const hr = Number(h); const ap = hr>=12?'PM':'AM'; return `${hr%12||12}:${m} ${ap}`;
  }
  getSubjectsForExam(examId: number): string[] {
    const e = this.exams().find(x => x.id === examId);
    return e?.schedule.map(s => s.subject) ?? [];
  }
  getMaxForSubject(examId: number, subject: string): number {
    return this.exams().find(e => e.id === examId)?.schedule.find(s => s.subject === subject)?.max_marks ?? 100;
  }

  getDayName(dateStr: string): string {
    if (!dateStr) return '—';
    return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(dateStr).getDay()];
  }

  getDayNameShort(dateStr: string): string {
    if (!dateStr) return '—';
    return ['SUN','MON','TUE','WED','THU','FRI','SAT'][new Date(dateStr).getDay()];
  }

  mathMax(...args: number[]): number { return Math.max(...args); }
  mathMin(...args: number[]): number { return Math.min(...args); }
  mathRound(n: number): number { return Math.round(n); }

  getClassAvg(list: StudentResult[]): number {
    return list.length ? Math.round(list.reduce((s,r) => s + r.percentage, 0) / list.length) : 0;
  }
  getHighest(list: StudentResult[]): number { return list.length ? Math.max(...list.map(r => r.percentage)) : 0; }
  getLowest(list: StudentResult[]): number  { return list.length ? Math.min(...list.map(r => r.percentage)) : 0; }
  getToppers(list: StudentResult[], n = 5): StudentResult[] {
    return [...list].sort((a, b) => b.percentage - a.percentage).slice(0, n);
  }
  getPassCount(list: StudentResult[]): number { return list.filter(r => r.result === 'Pass').length; }
  getPassRate(list: StudentResult[]): number  { return list.length ? Math.round((this.getPassCount(list)/list.length)*100) : 0; }
  getPassArc(list: StudentResult[]): number   { return list.length ? (this.getPassCount(list)/list.length)*251.3 : 0; }
  getMaxGradeCount(list: StudentResult[]): number {
    const grades = ['A+','A','B+','B','C+','C','D','F'];
    return Math.max(1, ...grades.map(g => list.filter(r => r.grade === g).length));
  }
  getGradeCount(list: StudentResult[], grade: string): number { return list.filter(r => r.grade === grade).length; }
  getSubjectAvg(list: StudentResult[], subject: string, maxM: number): number {
    const sms = list.flatMap(r => r.subject_marks.filter(sm => sm.subject === subject && !sm.is_absent && sm.marks_obtained !== null));
    return sms.length && maxM > 0
      ? Math.round((sms.reduce((s,sm) => s + (sm.marks_obtained ?? 0), 0) / sms.length / maxM) * 100)
      : 0;
  }
  getSubjectAvgRaw(list: StudentResult[], subject: string, maxM: number): number {
    const sms = list.flatMap(r => r.subject_marks.filter(sm => sm.subject === subject && !sm.is_absent && sm.marks_obtained !== null));
    return sms.length ? Math.round(sms.reduce((s,sm) => s + (sm.marks_obtained ?? 0), 0) / sms.length) : 0;
  }

  getMarksheetExam(r: StudentResult | null): Exam | undefined {
    if (!r) return undefined;
    return this.exams().find(e => e.id === r.exam_id);
  }

  private flash(m: string): void { this.saveSuccess.set(m); setTimeout(()=>this.saveSuccess.set(''),2500); }

  // ── SEED DATA ─────────────────────────────────────────
  // private buildExams(): Exam[] {
  //   return [
  //     {
  //       id:1, exam_name:'Mid-Term Examination 2025-26', exam_type:'Mid-Term', class_name:'Class XI – Science',
  //       academic_year:'2025-26', start_date:'2026-07-20', end_date:'2026-07-28', description:'Mid-term examination covering syllabus up to Unit 3',
  //       status:'Results Declared', created_at:'2026-07-01',
  //       schedule:[
  //         {subject:'Physics',     date:'2026-07-20',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Room 101'},
  //         {subject:'Chemistry',   date:'2026-07-22',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Room 102'},
  //         {subject:'Mathematics', date:'2026-07-24',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Room 101'},
  //         {subject:'Biology',     date:'2026-07-25',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Lab 3'},
  //         {subject:'English',     date:'2026-07-28',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Room 103'},
  //       ],
  //     },
  //     {
  //       id:2, exam_name:'Unit Test 1 – 2025-26', exam_type:'Unit Test', class_name:'Class IX',
  //       academic_year:'2025-26', start_date:'2026-08-05', end_date:'2026-08-09', description:'First Unit Test for Class IX',
  //       status:'Upcoming', created_at:'2026-07-15',
  //       schedule:[
  //         {subject:'Mathematics',  date:'2026-08-05',start_time:'09:00',duration:90,max_marks:50,pass_marks:17,room_no:'Room 201'},
  //         {subject:'Science',      date:'2026-08-06',start_time:'09:00',duration:90,max_marks:50,pass_marks:17,room_no:'Room 201'},
  //         {subject:'English',      date:'2026-08-07',start_time:'09:00',duration:90,max_marks:50,pass_marks:17,room_no:'Room 201'},
  //         {subject:'Hindi',        date:'2026-08-08',start_time:'09:00',duration:90,max_marks:50,pass_marks:17,room_no:'Room 201'},
  //         {subject:'Social Science',date:'2026-08-09',start_time:'09:00',duration:90,max_marks:50,pass_marks:17,room_no:'Room 201'},
  //       ],
  //     },
  //     {
  //       id:3, exam_name:'Final Annual Exam 2024-25', exam_type:'Final / Annual', class_name:'Class XI – Science',
  //       academic_year:'2024-25', start_date:'2025-03-10', end_date:'2025-03-28', description:'Final annual examination for session 2024-25',
  //       status:'Results Declared', created_at:'2025-02-01',
  //       schedule:[
  //         {subject:'Physics',     date:'2025-03-10',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Exam Hall'},
  //         {subject:'Chemistry',   date:'2025-03-13',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Exam Hall'},
  //         {subject:'Mathematics', date:'2025-03-17',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Exam Hall'},
  //         {subject:'Biology',     date:'2025-03-20',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Lab 3'},
  //         {subject:'English',     date:'2025-03-24',start_time:'09:00',duration:180,max_marks:100,pass_marks:33,room_no:'Exam Hall'},
  //         {subject:'Physical Education',date:'2025-03-28',start_time:'09:00',duration:120,max_marks:100,pass_marks:33,room_no:'Gymnasium'},
  //       ],
  //     },
  //   ];
  // }

  // private buildResults(): StudentResult[] {
  //   const exam1 = this.makeExamResults(1, 'Class XI – Science',
  //     ['Physics','Chemistry','Mathematics','Biology','English'],
  //     [100,100,100,100,100], [33,33,33,33,33],
  //     [
  //       {id:'S001',name:'Arjun Sharma',   roll:'11-A-01', marks:[78,82,91,75,88]},
  //       {id:'S002',name:'Priya Mehta',    roll:'11-A-02', marks:[92,88,95,90,85]},
  //       {id:'S006',name:'Kavita Yadav',   roll:'11-A-03', marks:[65,70,72,68,75]},
  //       {id:'S013',name:'Meena Sharma',   roll:'11-A-04', marks:[45,50,55,42,60]},
  //       {id:'S014',name:'Vikram Patel',   roll:'11-A-05', marks:[98,95,99,92,96]},
  //       {id:'S015',name:'Sunita Dubey',   roll:'11-A-06', marks:[75,78,80,72,82]},
  //       {id:'S016',name:'Ravi Gupta',     roll:'11-A-07', marks:[30,28,35,32,40]},
  //       {id:'S017',name:'Ananya Kapoor',  roll:'11-A-08', marks:[88,84,90,86,89]},
  //       {id:'S018',name:'Rahul Mishra',   roll:'11-A-09', marks:[20,25,22,18,30]},
  //       {id:'S019',name:'Deepak Singh',   roll:'11-A-10', marks:[82,78,85,80,87]},
  //     ], true, 1);

  //   const exam2 = this.makeExamResults(2, 'Class IX',
  //     ['Mathematics','Science','English','Hindi','Social Science'],
  //     [50,50,50,50,50], [17,17,17,17,17],
  //     [
  //       {id:'S101',name:'Rohan Verma',    roll:'09-A-01', marks:[42,38,44,40,36]},
  //       {id:'S102',name:'Ishita Nair',    roll:'09-A-02', marks:[48,46,45,47,44]},
  //       {id:'S103',name:'Aman Joshi',     roll:'09-A-03', marks:[30,28,33,31,29]},
  //       {id:'S104',name:'Neha Bansal',    roll:'09-A-04', marks:[12,15,18,14,16]},
  //       {id:'S105',name:'Kabir Singh',    roll:'09-A-05', marks:[40,41,39,42,38]},
  //     ], false, 11);

  //   const exam3 = this.makeExamResults(3, 'Class XI – Science',
  //     ['Physics','Chemistry','Mathematics','Biology','English','Physical Education'],
  //     [100,100,100,100,100,100], [33,33,33,33,33,33],
  //     [
  //       {id:'S001',name:'Arjun Sharma',   roll:'11-A-01', marks:[81,84,90,78,86,88]},
  //       {id:'S002',name:'Priya Mehta',    roll:'11-A-02', marks:[94,90,96,91,88,92]},
  //       {id:'S006',name:'Kavita Yadav',   roll:'11-A-03', marks:[70,74,76,71,78,80]},
  //       {id:'S014',name:'Vikram Patel',   roll:'11-A-05', marks:[96,97,99,94,95,93]},
  //       {id:'S017',name:'Ananya Kapoor',  roll:'11-A-08', marks:[86,85,91,84,90,88]},
  //       {id:'S016',name:'Ravi Gupta',     roll:'11-A-07', marks:[32,30,38,34,42,55]},
  //     ], true, 16);

  //   return this.reassignRanks([...exam1, ...exam2, ...exam3]);
  // }

  private makeExamResults(
    examId: number,
    className: string,
    subjects: string[],
    maxMarks: number[],
    passMarks: number[],
    students: {id:string; name:string; roll:string; marks:number[]}[],
    published: boolean,
    startId: number,
  ): StudentResult[] {
    return students.map((st, i) => {
      const sms: SubjectMark[] = subjects.map((s, j) => {
        const mo = st.marks[j];
        const pct = Math.round((mo / maxMarks[j]) * 100);
        return { subject:s, max_marks:maxMarks[j], pass_marks:passMarks[j], marks_obtained:mo, is_absent:false, grade:this.calcGrade(pct), remarks:'' };
      });
      const total = sms.reduce((s,m) => s + (m.marks_obtained??0), 0);
      const maxTotal = sms.reduce((s,m) => s + m.max_marks, 0);
      const pct = Math.round((total/maxTotal)*100);
      const allPass = sms.every(m => (m.marks_obtained??0) >= m.pass_marks);
      return {
        id: startId + i,
        exam_id: examId,
        student_id: st.id,
        student_name: st.name,
        class_name: className,
        roll_no: st.roll,
        subject_marks: sms,
        total_marks: total,
        max_total: maxTotal,
        percentage: pct,
        grade: this.calcGrade(pct),
        rank: 0,
        result: (allPass ? 'Pass' : 'Fail') as 'Pass'|'Fail',
        remarks: '',
        is_published: published,
      };
    });
  }

  // ── ACTIONS ─────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

  onSearchStd(marks: any): void {
    this.dataService.getSingleRecord('admissions', 'rno='+marks.roll_no).then((res: any) => {
      if(res) {
        const std: any = res;

        marks.student_name = std.std_name;
        marks.student_id = std.adm_code;
      }
    });
  }

}
